import React, { FC, useMemo } from 'react'
import { useEffectOnce } from 'react-use'
import { Card, makeStyles } from 'transfix-ui/core'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { DedicatedLaneAdditionalNotes } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLaneAdditionalNotes'
import { DedicatedLaneFacilityDetails } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLaneFacilityDetails'
import { DedicatedLanesBidForm } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesBidForm'
import { DedicatedLanesLoadDetails } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesLoadDetails'
import { DedicatedLanesSummaryCard } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesSummaryCard'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useGetDedicatedLane } from 'apps/marketplace/apiTypes/dedicatedLanes'

interface IDedicatedLanesBidSubpageProps {
  /** Dedicated lane (ShipperRate) id */
  laneId: string
}

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    padding: '29px 32px',
    gap: '24px',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
  },
  commitmentDetailsSection: {
    width: '60%',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  bidSection: {
    width: '40%',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
}))

/** This component is the main content of the dedicated lanes bid view.
 * It can be contained by either DedicatedLanesBidPage or DedicatedLanesBidModal.
 */
export const DedicatedLanesBidSubpage: FC<IDedicatedLanesBidSubpageProps> = ({ laneId }) => {
  const classes = useStyles()
  const { trackEvent } = useFMSAnalytics()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isListingDetailsEnabled } = getFlag('fmsDedicatedLaneListingDetails')

  const { data: laneData, loading: loadingLaneData } = useGetDedicatedLane({
    base: __API__,
    id: laneId,
  })
  const dedicatedLane = useMemo(() => (loadingLaneData ? undefined : laneData?.dedicatedLane), [
    laneData,
    loadingLaneData,
  ])
  const { averageAmount, id } = dedicatedLane || {}

  // Submit analytics events
  useEffectOnce(() => {
    trackEvent('Dedicated Lanes', 'VIEWED_DEDICATED_LANE_DETAILS')
  })

  return (
    <div className={classes.container}>
      {/* Lane Information */}
      <div className={classes.commitmentDetailsSection}>
        <div className={classes.marginBottom}>
          <Card>
            <DedicatedLanesSummaryCard dedicatedLane={dedicatedLane} />
          </Card>
        </div>

        <div className={classes.marginBottom}>
          <DedicatedLanesLoadDetails dedicatedLane={dedicatedLane} showMiles textSize='large' />
        </div>

        {isListingDetailsEnabled && (
          <>
            <div className={classes.marginBottom}>
              <DedicatedLaneFacilityDetails dedicatedLane={dedicatedLane} />
            </div>

            <div className={classes.marginBottom}>
              <DedicatedLaneAdditionalNotes dedicatedLane={dedicatedLane} />
            </div>
          </>
        )}
      </div>
      {/* Bid Form */}
      <div className={classes.bidSection}>
        <Card>
          <DedicatedLanesBidForm dedicatedLaneAvgAmount={averageAmount} dedicatedLaneId={id} />
        </Card>
      </div>
    </div>
  )
}
